import axios from 'axios';

/**
 * Mollie / Slim Innen Service
 *
 * Wraps the `/api/integrations/mollie/*` and Mollie-related invoice endpoints
 * documented in the backend integration README. All endpoints (except the
 * public payment endpoints in `invoiceService`) require an authenticated
 * session and a `slim_innen` license — the backend returns 403 / 422 when
 * those constraints are not met.
 */
export const mollieService = {
  /**
   * Returns the OAuth authorize URL the SPA must open in a popup window.
   * @returns {Promise<{ url: string }>}
   */
  async getAuthorizeUrl() {
    const response = await axios.get('/integrations/mollie');
    return response.data;
  },

  /**
   * Returns the ground-truth Mollie connection state, including KYC /
   * onboarding info. Use this on the integration detail screen — the global
   * `/invoices/trigger-availability` endpoint only returns a slim summary.
   *
   * Returns `{ connected: false, reason }` when not connected.
   */
  async getStatus() {
    const response = await axios.get('/integrations/mollie/status');
    return response.data;
  },

  /**
   * Disconnects Mollie. Always show a confirmation dialog first — clients
   * cannot pay invoices online via Mollie afterwards.
   */
  async disconnect() {
    const response = await axios.delete('/integrations/mollie');
    return response.data;
  },

  /**
   * Generate a Mollie checkout URL + QR for an existing invoice. Used to
   * give admins a copy-to-clipboard link they can drop in WhatsApp/email.
   *
   * @param {string} invoiceId
   * @param {Object} [body]
   * @param {string[]} [body.methods]      e.g. ['ideal', 'creditcard']
   * @param {number} [body.amount]         Override invoice balance_due
   * @param {string} [body.description]
   * @param {string} [body.redirect_url]
   * @param {boolean} [body.include_qr=true]
   * @returns {Promise<{
   *   payment_id: string,
   *   checkout_url: string,
   *   status: string,
   *   amount: { value: string, currency: string },
   *   expires_at: string,
   *   qr_data_url?: string,
   * }>}
   */
  async createPaymentLink(invoiceId, body = {}) {
    const payload = { include_qr: true, ...body };
    const response = await axios.post(`/invoices/${invoiceId}/payment-link`, payload);
    return response.data;
  },

  /**
   * Public — kicks off a Mollie payment from the public invoice page.
   * Rate-limited (10/min/IP+token) on the backend. Anonymous; no Bearer
   * token required.
   *
   * @param {string} token  invoice.public_token
   * @returns {Promise<{ checkout_url: string, amount: { value: string, currency: string } }>}
   */
  async startPublicPayment(token) {
    const response = await axios.post(`/invoices/public/${token}/pay`, {});
    return response.data;
  },
};

export default mollieService;
